import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PlayingCard from './PlayingCard';
import { sounds } from '@/audio/sounds';

/**
 * Double-Chance reveal sequence — used for BOTH:
 *
 *   mode='attack'  — your Joker fires at an opponent atk/def card.
 *                    Resolution: 2 offensive draws; either defeats target → SUCCESS.
 *   mode='defense' — opponent attacks YOUR placed Double-Chance card.
 *                    Resolution: 2 defensive draws; either defeats attacker → BLOCKED.
 *
 * Steps:
 *   0 — primary card vs target (the "matchup" frame, ~1100 ms)
 *   1 — first drawn card revealed alongside primary/target (~1300 ms)
 *   2 — second drawn card revealed alongside the rest    (~1400 ms)
 *   3 — full result frame: every card visible + outcome text (≥ MIN_RESULT_MS)
 *
 * Tap-to-skip CONTRACT:
 *   On skip from any step, jump to step 3 (final result frame). The result
 *   frame must remain visible for at least MIN_RESULT_MS (1000 ms) so the
 *   player can always see WHY the resolution played out (primary card +
 *   target/attacker + both draws + outcome text simultaneously).
 *
 * Safety: skippingRef + donePostedRef + dispatched-flag pattern — identical to
 * ShotSequence / JokerSequence — guarantees no double onDone, no duplicate
 * sound, no stuck overlay under rapid taps.
 *
 * Props:
 *   sequence: {
 *     mode: 'attack' | 'defense',
 *     primaryCard: Card,        // joker (attack) or attacker card (defense)
 *     targetCard: Card,         // opp atk/def card (attack) or DC card (defense)
 *     draws: Card[],            // flat draw list (legacy)
 *     attackerDraws?: Card[],   // DC vs DC: attacker side (Phase 3.8.1)
 *     defenderDraws?: Card[],   // DC vs DC: defender side (Phase 3.8.1)
 *     resultPositive: boolean,  // green text when true, red when false
 *     resultLabel: string,      // 'TARGET REMOVED' | 'TARGET SURVIVED' | 'BLOCKED' | 'DC REMOVED'
 *   }
 *   onDone: callback when sequence completes
 */
const RESULT_STEP = 3;
const TOTAL_STEPS = 4;
const MIN_RESULT_MS = 1000;
/** Compensates for AnimatePresence mode="wait" exit duration (~300-350 ms) on
 * the previous step. Ensures the final result frame stays visually mounted
 * for the full MIN_RESULT_MS after skip. */
const SKIP_LEAD_IN_MS = 400;
const DURATIONS = [1100, 1300, 1400, 1500];

export default function DoubleChanceSequence({ sequence, onDone }) {
  const [step, setStep] = useState(0);
  const skippingRef = useRef(false);
  const timerRef = useRef(null);
  const donePostedRef = useRef(false);
  // Stabilise onDone so frequent parent re-renders never re-fire the
  // step effect (otherwise sounds replay on every render).
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    if (!sequence) return;
    setStep(0);
    skippingRef.current = false;
    donePostedRef.current = false;
  }, [sequence?.primaryCard?.id, sequence?.targetCard?.id]);

  useEffect(() => {
    if (!sequence) return;
    if (step >= TOTAL_STEPS) {
      if (!donePostedRef.current) {
        donePostedRef.current = true;
        onDoneRef.current?.();
      }
      return;
    }
    // Lazy second draw: if only 1 draw happened (first succeeded OR deck empty),
    // skip step 2 — we go directly from step 1 to step 3 (result).
    if (step === 2 && (!sequence.draws || sequence.draws.length < 2)) {
      setStep(3);
      return;
    }
    if (step === 1 || step === 2) sounds.attackWin();
    if (step === RESULT_STEP) {
      if (sequence.resultPositive) sounds.goal();
      else sounds.save();
    }
    const dur = (step === RESULT_STEP && skippingRef.current) ? (MIN_RESULT_MS + SKIP_LEAD_IN_MS) : DURATIONS[step];
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStep((s) => s + 1), dur);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleSkip = () => {
    if (skippingRef.current) return;
    skippingRef.current = true;
    if (step < RESULT_STEP) {
      setStep(RESULT_STEP);
    } else if (step === RESULT_STEP) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setStep((s) => s + 1), MIN_RESULT_MS);
    }
  };

  if (!sequence) return null;

  const { mode, primaryCard, targetCard, draws, attackerDraws, defenderDraws, resultPositive, resultLabel } = sequence;
  // DC vs DC mode: both sides have their own draw pools. Renders a 2-side
  // layout so the player always sees attacker AND defender cards (Phase 3.8.1).
  const twoSided = Array.isArray(attackerDraws) && Array.isArray(defenderDraws);
  const primaryLabel = mode === 'attack' ? 'JOKER' : 'ATTACKER';
  const targetLabel  = mode === 'attack' ? 'TARGET' : 'DOUBLE CHANCE';
  const drawsLabel   = mode === 'attack' ? 'YOUR DRAWS' : 'DEFENSE DRAWS';
  const titleLabel   = twoSided
    ? 'DOUBLE CHANCE vs DOUBLE CHANCE'
    : (mode === 'attack' ? 'DOUBLE CHANCE — ATTACK' : 'DOUBLE CHANCE — DEFENSE');

  const resultColor = resultPositive ? 'hsl(var(--k-green))' : 'hsl(var(--k-red))';
  const resultShadow = resultPositive
    ? '0 0 28px hsl(var(--k-green) / 0.85)'
    : '0 0 28px hsl(var(--k-red) / 0.85)';

  return (
    <div
      onClick={handleSkip}
      role="button"
      tabIndex={-1}
      aria-label="Skip Double Chance reveal"
      data-testid="dc-sequence"
      className="absolute inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center cursor-pointer select-none px-4"
    >
      <div className="font-heading text-[10px] tracking-[0.32em] text-[hsl(var(--k-gold-bright))] uppercase mb-3">
        {titleLabel}
      </div>

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex items-center gap-4">
            <CardWithLabel card={primaryCard} label={primaryLabel} highlight size={78} />
            <div className="font-heading text-2xl text-white/45">VS</div>
            <CardWithLabel card={targetCard} label={targetLabel} size={78} />
          </motion.div>
        )}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-3 opacity-80">
              <CardWithLabel card={primaryCard} label={primaryLabel} size={56} />
              <div className="font-heading text-base text-white/45">VS</div>
              <CardWithLabel card={targetCard} label={targetLabel} size={56} />
            </div>
            <div className="font-heading text-[10px] tracking-[0.28em] text-white/55 uppercase">{drawsLabel}</div>
            <div className="flex items-center gap-3">
              <motion.div initial={{ rotateY: 180, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} transition={{ duration: 0.45 }} data-testid="dc-draw-1">
                <CardWithLabel card={draws[0]} label="DRAW 1" highlight size={78} />
              </motion.div>
            </div>
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-3 opacity-80">
              <CardWithLabel card={primaryCard} label={primaryLabel} size={56} />
              <div className="font-heading text-base text-white/45">VS</div>
              <CardWithLabel card={targetCard} label={targetLabel} size={56} />
            </div>
            <div className="font-heading text-[10px] tracking-[0.28em] text-white/55 uppercase">{drawsLabel}</div>
            <div className="flex items-center gap-3">
              <div className="opacity-65"><CardWithLabel card={draws[0]} label="DRAW 1 — FAIL" size={64} /></div>
              <motion.div initial={{ rotateY: 180, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} transition={{ duration: 0.45 }} data-testid="dc-draw-2">
                <CardWithLabel card={draws[1]} label="DRAW 2" highlight size={78} />
              </motion.div>
            </div>
          </motion.div>
        )}
        {step === 3 && (
          <motion.div
            key="s3"
            data-testid="dc-result-frame"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="flex items-center gap-3" data-testid="dc-result-matchup">
              <CardWithLabel card={primaryCard} label={primaryLabel} highlight size={62} />
              <div className="font-heading text-lg text-white/45">VS</div>
              <CardWithLabel card={targetCard} label={targetLabel} size={62} />
            </div>
            {twoSided ? (
              <div className="flex flex-col items-center gap-2 w-full max-w-[360px]">
                <DcSidePanel
                  label="ATTACKER"
                  testid="dc-result-attacker"
                  cards={attackerDraws}
                  highlightBest
                />
                <div className="font-heading text-[10px] tracking-[0.28em] text-white/45 uppercase">vs</div>
                <DcSidePanel
                  label="TARGET"
                  testid="dc-result-defender"
                  cards={defenderDraws}
                  highlightBest
                />
              </div>
            ) : (
              <div className="flex items-center gap-3" data-testid="dc-result-draws">
                {draws.map((d, i) => (
                  <CardWithLabel key={i} card={d} label={`DRAW ${i + 1}`} size={62} />
                ))}
              </div>
            )}
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
              className="font-heading uppercase"
              data-testid={`dc-result-${resultPositive ? 'positive' : 'negative'}`}
              style={{
                fontSize: 38,
                letterSpacing: '0.08em',
                color: resultColor,
                textShadow: resultShadow,
                lineHeight: 1,
              }}
            >
              {resultLabel}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CardWithLabel({ card, label, highlight, size = 78 }) {
  if (!card) {
    return (
      <div className="flex flex-col items-center gap-1">
        <CardPlaceholder size={size} />
        <span className="font-mono text-[8px] tracking-[0.22em] text-white/40 uppercase">{label}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1">
      <div style={{ width: size }}>
        <PlayingCard card={card} highlight={highlight} />
      </div>
      <span className="font-mono text-[8px] tracking-[0.22em] text-white/55 uppercase">{label}</span>
    </div>
  );
}

function CardPlaceholder({ size = 78 }) {
  return (
    <div
      className="kc-slot is-empty"
      style={{ width: size, aspectRatio: '2/3' }}
    >
      <div className="flex flex-col items-center justify-center opacity-50">
        <span className="line-label">—</span>
      </div>
    </div>
  );
}

/** Compare two cards numerically (A=14, K=13, etc.) — UI-only highlight. */
function cardValue(c) {
  if (!c || c.isJoker) return -1;
  const r = c.rank;
  if (r === 'A') return 14;
  if (r === 'K') return 13;
  if (r === 'Q') return 12;
  if (r === 'J') return 11;
  const n = parseInt(r, 10);
  return Number.isFinite(n) ? n : 0;
}

function bestIndex(cards) {
  let bestIdx = -1;
  let bestVal = -1;
  for (let i = 0; i < cards.length; i++) {
    const v = cardValue(cards[i]);
    if (v > bestVal) { bestVal = v; bestIdx = i; }
  }
  return bestIdx;
}

/**
 * One side of a DC-vs-DC result panel. Renders all the side's drawn cards
 * (including joker expansion) and highlights the best non-joker as the
 * card that counts. (Phase 3.8.1)
 */
function DcSidePanel({ label, testid, cards, highlightBest = false }) {
  const list = Array.isArray(cards) ? cards : [];
  const bestI = highlightBest ? bestIndex(list) : -1;
  return (
    <div className="w-full" data-testid={testid}>
      <div className="font-heading text-[9px] tracking-[0.26em] text-[hsl(var(--k-gold-bright))] uppercase mb-1.5">
        {label}
      </div>
      {list.length === 0 ? (
        <div className="font-mono text-[10px] text-white/40 px-1.5">no draws</div>
      ) : (
        <div className="flex flex-wrap items-end gap-1.5">
          {list.map((c, i) => (
            <CardWithLabel
              key={i}
              card={c}
              label={i === bestI ? 'BEST' : `DRAW ${i + 1}`}
              highlight={i === bestI}
              size={48}
            />
          ))}
        </div>
      )}
    </div>
  );
}
